// US6559 Phase 1 — "RB Pay Setup Required" gate modal, shown on the shift apply
// page when a worker taps Apply on an RB-Pay-verified location but isn't set up.
// Two variants (see evaluateRBPayApplyGate in util/rbPay.ts):
//   variant='setup'        -> worker can self-serve: Close + Setup RB Pay buttons.
//   variant='contactAdmin' -> worker isn't RB-Pay-enabled by their admin: single ok
//                             button; body asks them to contact the network admin.
//   variant='onHold'       -> worker's RB Pay account is On Hold (admin must lift it):
//                             single ok button; body asks them to contact the Network Admin.
import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { RBButton } from "../../components/RBButton";
import { renderRBPayText } from "../../components/RBPayWordmark";
import { UIRelatedConstants } from "../../theme/Theme";
import { RBPayApplyGateModal } from "../../util/rbPay";

interface RBPaySetupRequiredModalProps {
  open: boolean;
  variant: RBPayApplyGateModal;
  contactEmail?: string;
  primaryColor?: string;
  secondaryColor?: string;
  onClose: () => void;
  onSetup: () => void;
}

const TITLE = "RB Pay Setup Required";
const ON_HOLD_TITLE = "RB Pay On Hold";
const SETUP_BODY =
  "This office is set up for RB Pay to facilitate seamless payments with workers. Please complete your RB Pay profile in order to apply for this shift.";
const CONTACT_ADMIN_FALLBACK = "your network admin";

export const RBPaySetupRequiredModal: React.FC<RBPaySetupRequiredModalProps> = ({
  open,
  variant,
  contactEmail,
  primaryColor,
  secondaryColor,
  onClose,
  onSetup,
}) => {
  const isOnHold = variant === "onHold";
  const isContactAdmin = variant === "contactAdmin";
  // On Hold and contact-admin are both non-self-serve: single "ok" button, no Setup.
  const isSingleButton = isOnHold || isContactAdmin;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="rbpay-setup-required-title"
      PaperProps={{ style: { borderRadius: UIRelatedConstants.DialogBorderRadius } }}
    >
      <DialogTitle id="rbpay-setup-required-title" style={{ textAlign: "center" }}>
        {renderRBPayText(isOnHold ? ON_HOLD_TITLE : TITLE)}
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={{ textAlign: "center" }}>
          {isOnHold ? (
            <>
              Your <RBPayInline /> account is on hold. Please contact the Network
              Admin
              {contactEmail ? (
                <>
                  {" "}
                  <strong>{contactEmail}</strong>
                </>
              ) : null}{" "}
              for support.
            </>
          ) : isContactAdmin ? (
            <>
              This office uses <RBPayInline /> to facilitate payments. Please
              contact your network admin{" "}
              <strong>{contactEmail || CONTACT_ADMIN_FALLBACK}</strong> about
              getting your profile setup for <RBPayInline />.
            </>
          ) : (
            renderRBPayText(SETUP_BODY)
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions style={{ justifyContent: "center", paddingBottom: 20 }}>
        {isSingleButton ? (
          <RBButton
            key="ok"
            text="ok"
            onClick={onClose}
            buttonBackgroundColor={primaryColor}
            color="primary"
          />
        ) : (
          <>
            <RBButton
              key="close"
              text="Close"
              onClick={onClose}
              buttonBackgroundColor={secondaryColor}
              color="primary"
            />
            <RBButton
              key="setup"
              text="Setup RB Pay"
              onClick={onSetup}
              buttonBackgroundColor={primaryColor}
              color="primary"
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Inline branded "RB Pay" for use mid-sentence in the contact-admin copy.
const RBPayInline: React.FC = () => (
  <>
    RB <span style={{ fontStyle: "italic" }}>Pay</span>
  </>
);

export default RBPaySetupRequiredModal;
